import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFinance } from '../context/FinanceContext';
import { Edit2, Trash2, Eye, Search } from 'lucide-react';
import Skeleton from '../components/Skeleton';
import { useToast } from '../context/ToastContext';
import Modal from '../components/Modal';

export default function AllMonths() {
  const { months, fetchRecentMonths, updateMonth, deleteMonth, loading } = useFinance();
  const [editingId, setEditingId] = useState(null);
  const [editIncome, setEditIncome] = useState('');
  const [filterText, setFilterText] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const { addToast } = useToast();

  useEffect(() => {
    fetchRecentMonths();
  }, [fetchRecentMonths]);

  const handleEdit = (m) => {
    setEditingId(m.id);
    setEditIncome(m.expected_income);
  };

  const handleSave = async (id) => {
    await updateMonth(id, { expected_income: parseFloat(editIncome) });
    setEditingId(null);
    addToast('Income updated successfully!');
  };

  const handleDelete = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (deleteConfirmId) {
      await deleteMonth(deleteConfirmId);
      addToast('Month deleted successfully!', 'success');
      setDeleteConfirmId(null);
    }
  };

  const filteredMonths = months.filter(m => {
    const searchStr = `${new Date(0, m.month - 1).toLocaleString('default', { month: 'long' })} ${m.year} ${m.status}`.toLowerCase();
    return searchStr.includes(filterText.toLowerCase());
  });

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-800">All Months</h1>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text"
              placeholder="Search month, year..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <Link to="/new" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm text-center">
            Plan New Month
          </Link>
        </div>
      </div>
      
      {loading ? (
        <>
          {/* Mobile Skeletons */}
          <div className="md:hidden flex flex-col gap-4 p-4 bg-gray-50">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-16" variant="circle" />
                </div>
                <div className="flex justify-between mb-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex justify-between mb-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex justify-end pt-3 border-t border-gray-100">
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Skeletons */}
          <div className="hidden md:block overflow-x-auto p-4">
            <div className="space-y-4">
              <div className="flex gap-4 border-b pb-2">
                <Skeleton className="h-5 w-1/4" />
                <Skeleton className="h-5 w-1/4" />
                <Skeleton className="h-5 w-1/4" />
                <Skeleton className="h-5 w-1/4" />
                <Skeleton className="h-5 w-1/4" />
              </div>
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-4 border-b pb-4 pt-2">
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="md:hidden flex flex-col gap-3 p-3 bg-gray-50">
            {filteredMonths.map(m => (
              <div key={m.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                <div className="flex justify-between items-start mb-2">
                  <Link to={`/month/${m.id}`} className="font-bold text-lg text-blue-600 hover:underline">
                    {new Date(0, m.month - 1).toLocaleString('default', { month: 'long' })} {m.year}
                  </Link>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                    {m.status}
                  </span>
                </div>
                
                {editingId === m.id ? (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-gray-500 text-sm">Income: K</span>
                    <input 
                      type="number" 
                      value={editIncome} 
                      onChange={(e) => setEditIncome(e.target.value)}
                      className="w-24 p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                      autoFocus
                    />
                    <button onClick={() => handleSave(m.id)} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Cancel</button>
                  </div>
                ) : (
                  <div className="text-gray-600 text-sm mb-1 flex justify-between">
                    <span>Income:</span>
                    <span className="font-medium text-gray-800">K{m.expected_income}</span>
                  </div>
                )}
                
                <div className="text-gray-600 text-sm mb-3 flex justify-between">
                  <span>Remaining:</span>
                  <span className="font-medium text-blue-600">{m.remaining !== undefined ? `K${m.remaining}` : '--'}</span>
                </div>
                
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <div className="flex gap-4">
                    <button onClick={() => handleEdit(m)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 font-medium">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => handleDelete(m.id)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 font-medium">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                  <Link to={`/month/${m.id}`} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-100 font-medium">
                    <Eye className="w-3.5 h-3.5" /> View
                  </Link>
                </div>
              </div>
            ))}
            {filteredMonths.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                No months found.
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm border-b border-gray-200">
                  <th className="p-4 font-medium whitespace-nowrap">Month</th>
                  <th className="p-4 font-medium">Income</th>
                  <th className="p-4 font-medium">Remaining</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMonths.map(m => (
                  <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="p-4 whitespace-nowrap">
                      <Link to={`/month/${m.id}`} className="font-medium text-blue-600 hover:underline">
                        {new Date(0, m.month - 1).toLocaleString('default', { month: 'long' })} {m.year}
                      </Link>
                    </td>
                    <td className="p-4 text-gray-700">
                      {editingId === m.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">K</span>
                          <input 
                            type="number" 
                            value={editIncome} 
                            onChange={(e) => setEditIncome(e.target.value)}
                            className="w-24 p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                          <button onClick={() => handleSave(m.id)} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Cancel</button>
                        </div>
                      ) : (
                        <span>K{m.expected_income}</span>
                      )}
                    </td>
                    <td className="p-4 font-medium text-blue-600">{m.remaining !== undefined ? `K${m.remaining}` : '--'}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${m.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                        {m.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => handleEdit(m)} className="text-gray-400 hover:text-blue-600" title="Edit Income">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(m.id)} className="text-gray-400 hover:text-red-600" title="Delete Month">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <Link to={`/month/${m.id}`} className="text-gray-400 hover:text-gray-600" title="View Month">
                          <Eye className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMonths.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500">No months found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal 
        isOpen={deleteConfirmId !== null} 
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Month"
      >
        <div className="text-gray-600 mb-6">
          Are you sure you want to delete this month? This will delete all associated budgets and debts.
        </div>
        <div className="flex justify-end gap-3">
          <button 
            onClick={() => setDeleteConfirmId(null)}
            className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded font-medium transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={confirmDelete}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded font-medium transition-colors shadow-sm"
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
